import { useEffect, useRef, useState } from "react";
import { createChart } from "lightweight-charts";
import { useRx } from "@/context/AuthContext";
import { useMarket } from "@/context/MarketContext";
import { IST_OFFSET } from "@/lib/format";

const TFS = [1, 5, 15];
const shift = (c) => ({ ...c, time: c.time + IST_OFFSET });

export default function LiveChart({ symbol, height = 380, defaultTf = 1 }) {
  const el = useRef(null);
  const seriesRef = useRef(null);
  const linesRef = useRef([]);
  const aggRef = useRef(null);
  const [tf, setTf] = useState(defaultTf);
  const { api } = useRx();
  const { quotes } = useMarket();

  useEffect(() => {
    const chart = createChart(el.current, {
      autoSize: true,
      layout: { background: { type: "solid", color: "transparent" }, textColor: "#A0AEC0", fontFamily: "JetBrains Mono" },
      grid: { vertLines: { color: "rgba(255,255,255,0.04)" }, horzLines: { color: "rgba(255,255,255,0.04)" } },
      rightPriceScale: { borderColor: "rgba(255,255,255,0.08)" },
      timeScale: { borderColor: "rgba(255,255,255,0.08)", timeVisible: true, secondsVisible: false },
      crosshair: { mode: 0 },
    });
    seriesRef.current = chart.addCandlestickSeries({
      upColor: "#00FF66", downColor: "#FF3366", borderVisible: false, wickUpColor: "#00FF66", wickDownColor: "#FF3366",
    });
    return () => { chart.remove(); seriesRef.current = null; };
  }, []);

  useEffect(() => {
    if (!symbol) return;
    let alive = true;
    const sec = tf * 60;
    const load = async (initial) => {
      const d = await api("get", `/chart/${symbol}?tf=${tf}`).catch(() => null);
      const s = seriesRef.current;
      if (!alive || !d || !s) return;
      if (initial) {
        const data = d.candles.map(shift);
        s.setData(data);
        aggRef.current = data.at(-1) || null;
      }
      const markers = d.markers.map((m) => ({
        time: Math.floor(m.opened_ts / sec) * sec + IST_OFFSET,
        position: m.direction === "LONG" ? "belowBar" : "aboveBar",
        color: m.direction === "LONG" ? "#00FF66" : "#FF3366",
        shape: m.direction === "LONG" ? "arrowUp" : "arrowDown",
        text: m.direction === "LONG" ? "BUY" : "SELL",
      })).filter((m, i, arr) => arr.findIndex((x) => x.time === m.time && x.shape === m.shape) === i).sort((a, b) => a.time - b.time);
      s.setMarkers(markers);
      linesRef.current.forEach((l) => s.removePriceLine(l));
      linesRef.current = [];
      if (d.active) {
        const mk = (price, color, title) => s.createPriceLine({ price, color, lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title });
        linesRef.current = [mk(d.active.entry, "#007AFF", "ENTRY"), mk(d.active.sl, "#FF3366", "SL"), mk(d.active.target, "#00FF66", "TGT")];
      }
    };
    load(true);
    const id = setInterval(() => load(false), 20000);
    return () => { alive = false; clearInterval(id); };
  }, [symbol, api, tf]);

  const candle = quotes[symbol]?.candle;
  useEffect(() => {
    const s = seriesRef.current;
    const agg = aggRef.current;
    if (!candle || !s || !agg) return;
    const t = Math.floor(candle.time / (tf * 60)) * tf * 60 + IST_OFFSET;
    let next;
    if (t === agg.time) next = { ...agg, high: Math.max(agg.high, candle.high), low: Math.min(agg.low, candle.low), close: candle.close };
    else if (t > agg.time) next = { time: t, open: candle.open, high: candle.high, low: candle.low, close: candle.close };
    else return;
    aggRef.current = next;
    s.update(next);
  }, [candle, tf]);

  return (
    <div className="relative">
      <div className="absolute right-2 top-2 z-10 inline-flex p-0.5 rounded-lg bg-black/60 border border-white/10 backdrop-blur" data-testid="tf-switch">
        {TFS.map((t) => (
          <button key={t} onClick={() => setTf(t)} data-testid={`tf-${t}m`}
            className={`px-3 py-1 rounded-md text-xs font-data font-bold transition-colors ${tf === t ? "bg-[#00FF66] text-black shadow-[0_0_14px_rgba(0,255,102,.45)]" : "text-gray-400 hover:text-white"}`}>
            {t}m
          </button>
        ))}
      </div>
      <div ref={el} style={{ height }} className="w-full" data-testid="live-chart" />
    </div>
  );
}
